/**
 * Owner: Masa Bayu
 * File: CommerceCartPanel.tsx
 * Date:Time: 2026-09-15 22:25
 * Description: T3-E1 cart UI — add/remove/qty; no payment charge.
 *   §466 S2/S3 — senarai tawaran dibaca daripada qxp-hub (katalog + harga
 *   descriptor), bukan carta harga rekaan: setiap butang yang dipaparkan
 *   dijamin boleh dilaksanakan oleh stor.
 */

import { useState } from 'react';
import { cartHasBlockingIssues } from '@qx/qxread-core';
import { formatMarketplacePriceOrUnknown } from '../home/marketplace/marketplace-locale';
import { QxEmptyState } from '../ux';
import {
  addDemoOfferToCart,
  getDemoCart,
  removeDemoCartLine,
  updateDemoCartQuantity,
} from './demo-cart-store';
import { listCatalogOffers } from './catalog-offers';

export interface CommerceCartPanelProps {
  readonly onNavigate: (path: string) => void;
}

function priceLabel(minorUnits: number, currency: string): string {
  return formatMarketplacePriceOrUnknown(minorUnits, currency, 'Price unavailable');
}

export function CommerceCartPanel({ onNavigate }: CommerceCartPanelProps) {
  const [, bump] = useState(0);
  const refresh = () => {
    bump((value) => value + 1);
  };
  const cart = getDemoCart();
  const offers = listCatalogOffers();

  return (
    <section className="route-panel" aria-labelledby="route-title">
      <p className="eyebrow">QXRead · T3-E1 Commerce Foundation</p>
      <h2 id="route-title">Cart</h2>
      <p>
        Cart holds price snapshots before checkout. Client price is not authority — Service Fee is
        buyer-side; Affiliate/Referral remain automatic contracts.
      </p>
      <p className="route-panel__meta" role="status">
        Session {cart.sessionKey} · Payment ≠ Reader · Entitlement not minted here
      </p>

      {cart.lines.length === 0 ? (
        <div>
          <QxEmptyState
            title="Your cart is empty"
            description="Discover publications, then add a title before checkout."
            actionLabel="Discover"
            onAction={() => {
              onNavigate('/discover');
            }}
          />
          {offers.length === 0 ? (
            <p role="status">
              No publication is on sale right now — the qxp-hub catalog has no priced offer to add.
            </p>
          ) : (
            <div>
              <p className="eyebrow">On sale now</p>
              <ul aria-label="Publications available for purchase">
                {offers.map((offer) => (
                  <li key={offer.publicationId}>
                    {offer.title} · {priceLabel(offer.sellerPriceMinorUnits, offer.currency)}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        addDemoOfferToCart(offer.publicationId);
                        refresh();
                      }}
                    >
                      Add to cart
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <ul className="discovery-rail__list" aria-label="Cart lines">
          {cart.lines.map((line) => (
            <li key={line.lineId}>
              <article aria-labelledby={`cart-${line.publicationId}`}>
                <h3 id={`cart-${line.publicationId}`}>{line.snapshot.title}</h3>
                <p>
                  Snapshot {priceLabel(line.snapshot.sellerPriceMinorUnits, line.snapshot.currency)}{' '}
                  · qty{' '}
                  <label>
                    Quantity
                    <input
                      type="number"
                      min={1}
                      value={line.quantity}
                      aria-label={`Quantity for ${line.snapshot.title}`}
                      onChange={(event) => {
                        const next = Number(event.target.value);
                        if (Number.isInteger(next) && next >= 1) {
                          updateDemoCartQuantity(line.publicationId, next);
                          refresh();
                        }
                      }}
                    />
                  </label>{' '}
                  · issue: {line.issue}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    removeDemoCartLine(line.publicationId);
                    refresh();
                  }}
                >
                  Remove
                </button>
              </article>
            </li>
          ))}
        </ul>
      )}

      {cartHasBlockingIssues(cart) ? (
        <p role="alert">Resolve unavailable or price-changed lines before checkout.</p>
      ) : null}

      <p>
        <a
          href="#/checkout"
          onClick={(event) => {
            event.preventDefault();
            onNavigate('/checkout');
          }}
        >
          Continue to checkout
        </a>
      </p>
    </section>
  );
}
